/** Marks a part of the screen the voice may read, named by its value. */
const ARC_SCREEN_ATTRIBUTE = 'data-arc-screen'

/** The parts of the screen the voice reads, by the name it sees them under. */
export enum ArcScreenSection {
  Page = 'Page',
  SidePanel = 'Side panel',
  TaskPanel = 'Task panel',
}

/** Spread on an element to have the voice read it; null while it isn't showing. */
export const arcScreenProps = (section: ArcScreenSection | null) => ({
  [ARC_SCREEN_ATTRIBUTE]: section ?? undefined,
})

/** Enough for a list's first rows or a record's header, without filling the voice's brief. */
const SCREEN_TEXT_MAX_CHARS = 6000
/** The links' share, on top of the text: the whole stays under both added together. */
const SCREEN_LINKS_MAX_CHARS = 3000

/** A changed screen is read again after this long, however busy the page is. */
const READ_DELAY_MS = 1500

/** Overlaid panels first: they are what the person is looking at when one is open. */
const SECTION_ORDER: string[] = [
  ArcScreenSection.TaskPanel,
  ArcScreenSection.SidePanel,
  ArcScreenSection.Page,
]

const compactText = (text: string): string =>
  text
    .replace(/[ \t\u00a0]+/g, ' ')
    .replace(/ *\n[\s]*/g, '\n')
    .trim()

const sectionRank = (name: string): number => {
  const rank = SECTION_ORDER.indexOf(name)
  return rank === -1 ? SECTION_ORDER.length : rank
}

/** Links listed after the text, so a row can be opened without looking its id up. */
const SCREEN_LINKS_MAX = 40
const LINK_LABEL_MAX_CHARS = 90

// jsdom has no innerText.
const visibleText = (element: HTMLElement): string =>
  compactText(element.innerText ?? element.textContent ?? '')

const oneLine = (text: string): string => {
  const line = text.replace(/\s+/g, ' ').trim()
  return line.length > LINK_LABEL_MAX_CHARS ? `${line.slice(0, LINK_LABEL_MAX_CHARS - 1)}…` : line
}

/** Where a row or link on screen goes, as an in-app path, or null for one that leaves the app. */
const linkTarget = (element: HTMLElement): string | null => {
  // A task row opens its panel on the page it's listed on.
  const taskId = element.getAttribute('data-task-id')
  if (taskId) return `${window.location.pathname}?taskId=${taskId}`
  const href = element.getAttribute('href')
  if (!href?.startsWith('/') || href.startsWith('//')) return null
  return href
}

/** As many whole link lines as fit the links' share; one cut mid-path would open nothing. */
const capLinks = (lines: string[]): string[] => {
  const kept: string[] = []
  let used = 0
  for (const line of lines) {
    if (used + line.length + 1 > SCREEN_LINKS_MAX_CHARS) break
    kept.push(line)
    used += line.length + 1
  }
  return kept
}

/**
 * Each link in a section, labelled by its whole row when it sits in one ("Funding ·
 * Elavi · Kivash"). Stops at `budget`: reading a row's text forces layout, and a long
 * table would otherwise pay for hundreds of rows on every read.
 */
const sectionLinks = (section: HTMLElement, budget: number): string[] => {
  const seen = new Set<string>()
  const lines: string[] = []
  for (const element of section.querySelectorAll<HTMLElement>('a[href], [data-task-id]')) {
    if (lines.length >= budget) break
    const target = linkTarget(element)
    if (!target || seen.has(target)) continue
    seen.add(target)
    const row = element.closest<HTMLElement>('tr, [role="row"]')
    const label = oneLine(visibleText(row ?? element) || element.getAttribute('aria-label') || '')
    if (label) lines.push(`- ${label} → ${target}`)
  }
  return lines
}

/**
 * What is on their screen as plain text, section by section, cut to a size the
 * voice can keep in mind, with where each row or link goes. Tables read row by
 * row, so "the third one" is answerable without a lookup, and opening it needs no
 * id search. Null when nothing marked is showing.
 */
export const readScreenText = (root: ParentNode = document): string | null => {
  const sections = [...root.querySelectorAll<HTMLElement>(`[${ARC_SCREEN_ATTRIBUTE}]`)]
    .map((element) => ({
      element,
      name: element.getAttribute(ARC_SCREEN_ATTRIBUTE) ?? ArcScreenSection.Page,
      text: visibleText(element),
    }))
    .filter(({ text }) => text)
    .sort((a, b) => sectionRank(a.name) - sectionRank(b.name))
  if (sections.length === 0) return null

  const body = sections.map(({ name, text }) => `[${name}]\n${text}`).join('\n\n')
  const text =
    body.length > SCREEN_TEXT_MAX_CHARS ? `${body.slice(0, SCREEN_TEXT_MAX_CHARS - 1)}…` : body
  const links: string[] = []
  for (const { element } of sections) {
    links.push(...sectionLinks(element, SCREEN_LINKS_MAX - links.length))
  }
  if (links.length === 0) return text
  return `${text}\n\n[Links on screen]\n${capLinks(links).join('\n')}`
}

const SCREEN_SELECTOR = `[${ARC_SCREEN_ATTRIBUTE}]`

const isInsideScreen = (node: Node): boolean => {
  const element = node instanceof Element ? node : node.parentElement
  return !!element?.closest(SCREEN_SELECTOR)
}

/** A panel mounting or unmounting whole: its parent is outside every marked part. */
const holdsScreen = (node: Node): boolean =>
  node instanceof Element &&
  (node.matches(SCREEN_SELECTOR) || !!node.querySelector(SCREEN_SELECTOR))

/** Only our attribute is observed, so any attribute change is a panel opening or closing. */
const touchesScreen = (record: MutationRecord): boolean =>
  record.type === 'attributes' ||
  isInsideScreen(record.target) ||
  [...record.addedNodes, ...record.removedNodes].some(holdsScreen)

/**
 * Calls `onChange` with the screen's text whenever a marked part of it changes,
 * at most once per read delay. Changes outside those parts (a ticking call clock)
 * are ignored, so an idle page costs nothing. Returns the stop function.
 */
export const watchScreenText = (
  onChange: (text: string | null) => void,
  readDelayMs = READ_DELAY_MS,
): (() => void) => {
  let lastText: string | null | undefined
  let timer: ReturnType<typeof setTimeout> | null = null

  const read = () => {
    timer = null
    const text = readScreenText()
    if (text === lastText) return
    lastText = text
    onChange(text)
  }
  const schedule = () => {
    timer ??= setTimeout(read, readDelayMs)
  }

  const observer = new MutationObserver((records) => {
    if (records.some(touchesScreen)) schedule()
  })
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: [ARC_SCREEN_ATTRIBUTE],
  })
  read()

  return () => {
    observer.disconnect()
    if (timer) clearTimeout(timer)
  }
}
